"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";
import { Check } from "lucide-react";

export type FilterDropdownOption = string | { value: string; label: string };

interface FilterDropdownProps {
  purpose: string;
  value: string;
  options: FilterDropdownOption[];
  onChange: (value: string) => void;
  width?: string;
  maxVisibleRows?: number;
  placeholder?: string;
  label?: string;
  showLabel?: boolean;
  triggerTestId?: string;
}

export default function FilterDropdown({
  purpose,
  value,
  options,
  onChange,
  width = "180px",
  maxVisibleRows,
  placeholder,
  label = "Status",
  showLabel = false,
  triggerTestId,
}: FilterDropdownProps) {
  const t = useTranslations("FilterDropdown");
  const resolvedPlaceholder = placeholder ?? t("placeholder");
  const resolvedOptions = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const selectedLabel =
    resolvedOptions.find((option) => option.value === value)?.label ?? value;

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={triggerTestId ?? domId("cbo", `${purpose}-trigger`)}
        className="h-10 appearance-none overflow-hidden whitespace-nowrap rounded-lg border border-slate-300 bg-white px-4 py-2 text-left text-sm font-medium leading-5 text-stone-950 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        style={{ minWidth: width, maxWidth: width }}
      >
        {/* Radix SelectValue drops its className, so the truncating box must be this wrapper. */}
        <span className="min-w-0 flex-1 truncate whitespace-nowrap">
          <SelectValue placeholder={resolvedPlaceholder}>
            {showLabel ? `${label} - ${selectedLabel}` : selectedLabel}
          </SelectValue>
        </span>
      </SelectTrigger>
      {/* The shared SelectContent shifts 4px on every side; the extra top shift keeps the
          8px pill gap identical when the menu flips above the trigger. */}
      <SelectContent
        className="mt-1 max-h-[min(400px,var(--radix-popper-available-height))] overflow-y-auto rounded-lg border-0 bg-white shadow-[0px_3px_12px_0px_rgba(0,0,0,0.13)] data-[side=top]:-translate-y-2 [&_[data-radix-select-viewport]]:max-h-[inherit]"
        style={{
          width: width === "100%" ? "var(--radix-select-trigger-width)" : width,
          maxHeight: maxVisibleRows
            ? `min(${maxVisibleRows * 40 + 8}px, var(--radix-popper-available-height))`
            : undefined,
        }}
      >
        {resolvedOptions.map((option) => (
          <SelectItem
            key={option.value}
            data-testid={domId("cbo", `${purpose}-option-${option.value}`)}
            value={option.value}
            showIndicator={false}
            className={`relative h-auto min-h-10 w-full whitespace-normal rounded-lg px-3 py-2 text-left text-sm font-normal leading-5 text-stone-950 transition-colors [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1 ${
              value === option.value ? "bg-zinc-100 pl-10" : "bg-white"
            }`}
          >
            {value === option.value && (
              <Check className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-lime-700" />
            )}
            <span className="block min-w-0 whitespace-normal break-words">{option.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
